/*
 * Strings for the CMMS tool register (features/cmmsTools).
 *
 * The service status and service history strings are NOT here. They live in
 * features/cmms/cmmsMessages.js with the other enum label helpers, because /tools and the machine
 * page name the same readings — a second copy of "Overdue" is how two screens end up disagreeing
 * about one tool.
 *
 * What is here is the page around them: its title, its tiles, its filters and its columns.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.tools";

export default defineMessages({
  // page
  pageTitle: { id: `${scope}.page.title`, defaultMessage: "Tools" },
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "Every tool in the register, how far each has run against its service schedule, and when it was last serviced.",
  },

  // statistic tiles. Upper-cased by CSS, not by the message — shouting at translators forces them
  // to guess whether the caps mean anything, and some locales have no upper case at all.
  statOverdue: { id: `${scope}.stat.overdue`, defaultMessage: "Overdue" },
  statDueSoon: { id: `${scope}.stat.dueSoon`, defaultMessage: "Due soon" },
  statWatched: { id: `${scope}.stat.watched`, defaultMessage: "Watched" },

  // filters. The search label names the fields it covers, like every other search box in the app.
  filterSearch: {
    id: `${scope}.filter.search`,
    defaultMessage: "Search by tool name, description or schedule",
  },
  filterWatchedOnly: { id: `${scope}.filter.watchedOnly`, defaultMessage: "Only watched tools" },
  filterAttentionOnly: { id: `${scope}.filter.attentionOnly`, defaultMessage: "Needs attention" },
  filterClear: { id: `${scope}.filter.clear`, defaultMessage: "Clear filters" },

  // grid columns. Running time and cycles are spelled out rather than abbreviated: a column
  // headed "h" next to one headed "st" is unreadable, and these two are the point of the page.
  columnName: { id: `${scope}.column.name`, defaultMessage: "Tool" },
  columnDescription: { id: `${scope}.column.description`, defaultMessage: "Description" },
  columnLevel: { id: `${scope}.column.level`, defaultMessage: "Level" },
  columnSchedule: { id: `${scope}.column.schedule`, defaultMessage: "Schedule" },
  columnTriggerBasis: { id: `${scope}.column.triggerBasis`, defaultMessage: "Basis" },
  columnRunningTimeHours: { id: `${scope}.column.runningTimeHours`, defaultMessage: "Running hours" },
  columnQuantity: { id: `${scope}.column.quantity`, defaultMessage: "Cycles" },
  columnRemaining: { id: `${scope}.column.remaining`, defaultMessage: "Remaining" },
  columnLastServiced: { id: `${scope}.column.lastServiced`, defaultMessage: "Last serviced" },
  columnScheduleCount: { id: `${scope}.column.scheduleCount`, defaultMessage: "Schedules" },

  // empty states
  emptyTitle: { id: `${scope}.empty.title`, defaultMessage: "No tools in the register" },
  emptyHint: {
    id: `${scope}.empty.hint`,
    defaultMessage: "Tools are created under Settings → Tools. They appear here as soon as they exist.",
  },
  emptyFilteredTitle: { id: `${scope}.emptyFiltered.title`, defaultMessage: "No tools match the filters" },
  emptyFilteredHint: {
    id: `${scope}.emptyFiltered.hint`,
    defaultMessage: "Clear the filters to see the whole register.",
  },

  // drawer
  drawerSchedules: { id: `${scope}.drawer.schedules`, defaultMessage: "Service schedules" },
  drawerHistory: { id: `${scope}.drawer.history`, defaultMessage: "Service history" },
  drawerOpen: { id: `${scope}.drawer.open`, defaultMessage: "Service status and history for {tool}" },

  // errors. Two reads, two sentences: which one failed decides what is still on screen.
  loadFailed: { id: `${scope}.loadFailed`, defaultMessage: "Could not load the tool register." },
  statusLoadFailed: {
    id: `${scope}.statusLoadFailed`,
    defaultMessage: "Could not load the service status. The tools below are listed without it.",
  },
});
